import { useState, useEffect, useRef, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MapPin, Search, X, Globe, Building2, MessageSquarePlus } from "lucide-react";
import SuggestPlaceModal from "./SuggestPlaceModal";

// Common destinations with coordinates
const POPULAR_DESTINATIONS = [
  { name: "Paris", country: "France", code: "FR", lat: 48.8566, lng: 2.3522 },
  { name: "Tokyo", country: "Japan", code: "JP", lat: 35.6762, lng: 139.6503 },
  { name: "New York", country: "United States", code: "US", lat: 40.7128, lng: -74.0060 },
  { name: "London", country: "United Kingdom", code: "GB", lat: 51.5074, lng: -0.1278 },
  { name: "Rome", country: "Italy", code: "IT", lat: 41.9028, lng: 12.4964 },
  { name: "Barcelona", country: "Spain", code: "ES", lat: 41.3851, lng: 2.1734 },
  { name: "Sydney", country: "Australia", code: "AU", lat: -33.8688, lng: 151.2093 },
  { name: "Dubai", country: "UAE", code: "AE", lat: 25.2048, lng: 55.2708 },
  { name: "Bali", country: "Indonesia", code: "ID", lat: -8.3405, lng: 115.0920 },
  { name: "Santorini", country: "Greece", code: "GR", lat: 36.3932, lng: 25.4615 },
  { name: "Machu Picchu", country: "Peru", code: "PE", lat: -13.1631, lng: -72.5450 },
  { name: "Maldives", country: "Maldives", code: "MV", lat: 3.2028, lng: 73.2207 },
  { name: "Safari (Serengeti)", country: "Tanzania", code: "TZ", lat: -2.3333, lng: 34.8333 },
  { name: "Grand Canyon", country: "United States", code: "US", lat: 36.1069, lng: -112.1129 },
  { name: "Great Wall", country: "China", code: "CN", lat: 40.4319, lng: 116.5704 },
  { name: "Petra", country: "Jordan", code: "JO", lat: 30.3285, lng: 35.4444 },
  { name: "Taj Mahal", country: "India", code: "IN", lat: 27.1751, lng: 78.0421 },
  { name: "Northern Lights (Tromsø)", country: "Norway", code: "NO", lat: 69.6492, lng: 18.9553 },
  { name: "Swiss Alps", country: "Switzerland", code: "CH", lat: 46.8182, lng: 8.2275 },
  { name: "Amalfi Coast", country: "Italy", code: "IT", lat: 40.6333, lng: 14.6029 },
  { name: "Cinque Terre", country: "Italy", code: "IT", lat: 44.1461, lng: 9.6439 },
];

// Major world cities organized by region
const CITIES = [
  // Europe
  { name: "London", country: "United Kingdom", code: "GB", lat: 51.5074, lng: -0.1278 },
  { name: "Paris", country: "France", code: "FR", lat: 48.8566, lng: 2.3522 },
  { name: "Rome", country: "Italy", code: "IT", lat: 41.9028, lng: 12.4964 },
  { name: "Barcelona", country: "Spain", code: "ES", lat: 41.3851, lng: 2.1734 },
  { name: "Madrid", country: "Spain", code: "ES", lat: 40.4168, lng: -3.7038 },
  { name: "Amsterdam", country: "Netherlands", code: "NL", lat: 52.3676, lng: 4.9041 },
  { name: "Berlin", country: "Germany", code: "DE", lat: 52.5200, lng: 13.4050 },
  { name: "Munich", country: "Germany", code: "DE", lat: 48.1351, lng: 11.5820 },
  { name: "Vienna", country: "Austria", code: "AT", lat: 48.2082, lng: 16.3738 },
  { name: "Prague", country: "Czech Republic", code: "CZ", lat: 50.0755, lng: 14.4378 },
  { name: "Lisbon", country: "Portugal", code: "PT", lat: 38.7223, lng: -9.1393 },
  { name: "Dublin", country: "Ireland", code: "IE", lat: 53.3498, lng: -6.2603 },
  { name: "Edinburgh", country: "United Kingdom", code: "GB", lat: 55.9533, lng: -3.1883 },
  { name: "Venice", country: "Italy", code: "IT", lat: 45.4408, lng: 12.3155 },
  { name: "Florence", country: "Italy", code: "IT", lat: 43.7696, lng: 11.2558 },
  { name: "Milan", country: "Italy", code: "IT", lat: 45.4642, lng: 9.1900 },
  { name: "Athens", country: "Greece", code: "GR", lat: 37.9838, lng: 23.7275 },
  { name: "Budapest", country: "Hungary", code: "HU", lat: 47.4979, lng: 19.0402 },
  { name: "Warsaw", country: "Poland", code: "PL", lat: 52.2297, lng: 21.0122 },
  { name: "Copenhagen", country: "Denmark", code: "DK", lat: 55.6761, lng: 12.5683 },
  { name: "Stockholm", country: "Sweden", code: "SE", lat: 59.3293, lng: 18.0686 },
  { name: "Oslo", country: "Norway", code: "NO", lat: 59.9139, lng: 10.7522 },
  { name: "Helsinki", country: "Finland", code: "FI", lat: 60.1699, lng: 24.9384 },
  { name: "Zurich", country: "Switzerland", code: "CH", lat: 47.3769, lng: 8.5417 },
  { name: "Geneva", country: "Switzerland", code: "CH", lat: 46.2044, lng: 6.1432 },
  { name: "Brussels", country: "Belgium", code: "BE", lat: 50.8503, lng: 4.3517 },
  { name: "Moscow", country: "Russia", code: "RU", lat: 55.7558, lng: 37.6173 },
  { name: "St. Petersburg", country: "Russia", code: "RU", lat: 59.9311, lng: 30.3609 },
  { name: "Istanbul", country: "Turkey", code: "TR", lat: 41.0082, lng: 28.9784 },
  { name: "Reykjavik", country: "Iceland", code: "IS", lat: 64.1466, lng: -21.9426 },
  
  // Asia
  { name: "Tokyo", country: "Japan", code: "JP", lat: 35.6762, lng: 139.6503 },
  { name: "Kyoto", country: "Japan", code: "JP", lat: 35.0116, lng: 135.7681 },
  { name: "Osaka", country: "Japan", code: "JP", lat: 34.6937, lng: 135.5023 },
  { name: "Seoul", country: "South Korea", code: "KR", lat: 37.5665, lng: 126.9780 },
  { name: "Beijing", country: "China", code: "CN", lat: 39.9042, lng: 116.4074 },
  { name: "Shanghai", country: "China", code: "CN", lat: 31.2304, lng: 121.4737 },
  { name: "Hong Kong", country: "Hong Kong", code: "HK", lat: 22.3193, lng: 114.1694 },
  { name: "Singapore", country: "Singapore", code: "SG", lat: 1.3521, lng: 103.8198 },
  { name: "Bangkok", country: "Thailand", code: "TH", lat: 13.7563, lng: 100.5018 },
  { name: "Phuket", country: "Thailand", code: "TH", lat: 7.8804, lng: 98.3923 },
  { name: "Chiang Mai", country: "Thailand", code: "TH", lat: 18.7883, lng: 98.9853 },
  { name: "Hanoi", country: "Vietnam", code: "VN", lat: 21.0278, lng: 105.8342 },
  { name: "Ho Chi Minh City", country: "Vietnam", code: "VN", lat: 10.8231, lng: 106.6297 },
  { name: "Kuala Lumpur", country: "Malaysia", code: "MY", lat: 3.1390, lng: 101.6869 },
  { name: "Mumbai", country: "India", code: "IN", lat: 19.0760, lng: 72.8777 },
  { name: "Delhi", country: "India", code: "IN", lat: 28.7041, lng: 77.1025 },
  { name: "Jaipur", country: "India", code: "IN", lat: 26.9124, lng: 75.7873 },
  { name: "Goa", country: "India", code: "IN", lat: 15.2993, lng: 74.1240 },
  { name: "Dubai", country: "UAE", code: "AE", lat: 25.2048, lng: 55.2708 },
  { name: "Abu Dhabi", country: "UAE", code: "AE", lat: 24.4539, lng: 54.3773 },
  { name: "Taipei", country: "Taiwan", code: "TW", lat: 25.0330, lng: 121.5654 },
  { name: "Manila", country: "Philippines", code: "PH", lat: 14.5995, lng: 120.9842 },
  { name: "Jakarta", country: "Indonesia", code: "ID", lat: -6.2088, lng: 106.8456 },
  { name: "Kathmandu", country: "Nepal", code: "NP", lat: 27.7172, lng: 85.3240 },
  { name: "Colombo", country: "Sri Lanka", code: "LK", lat: 6.9271, lng: 79.8612 },
  
  // Americas
  { name: "New York", country: "United States", code: "US", lat: 40.7128, lng: -74.0060 },
  { name: "Los Angeles", country: "United States", code: "US", lat: 34.0522, lng: -118.2437 },
  { name: "San Francisco", country: "United States", code: "US", lat: 37.7749, lng: -122.4194 },
  { name: "Las Vegas", country: "United States", code: "US", lat: 36.1699, lng: -115.1398 },
  { name: "Miami", country: "United States", code: "US", lat: 25.7617, lng: -80.1918 },
  { name: "Chicago", country: "United States", code: "US", lat: 41.8781, lng: -87.6298 },
  { name: "Boston", country: "United States", code: "US", lat: 42.3601, lng: -71.0589 },
  { name: "Seattle", country: "United States", code: "US", lat: 47.6062, lng: -122.3321 },
  { name: "Washington D.C.", country: "United States", code: "US", lat: 38.9072, lng: -77.0369 },
  { name: "New Orleans", country: "United States", code: "US", lat: 29.9511, lng: -90.0715 },
  { name: "Honolulu", country: "United States", code: "US", lat: 21.3069, lng: -157.8583 },
  { name: "Toronto", country: "Canada", code: "CA", lat: 43.6532, lng: -79.3832 },
  { name: "Vancouver", country: "Canada", code: "CA", lat: 49.2827, lng: -123.1207 },
  { name: "Montreal", country: "Canada", code: "CA", lat: 45.5017, lng: -73.5673 },
  { name: "Mexico City", country: "Mexico", code: "MX", lat: 19.4326, lng: -99.1332 },
  { name: "Cancun", country: "Mexico", code: "MX", lat: 21.1619, lng: -86.8515 },
  { name: "Havana", country: "Cuba", code: "CU", lat: 23.1136, lng: -82.3666 },
  { name: "Rio de Janeiro", country: "Brazil", code: "BR", lat: -22.9068, lng: -43.1729 },
  { name: "São Paulo", country: "Brazil", code: "BR", lat: -23.5505, lng: -46.6333 },
  { name: "Buenos Aires", country: "Argentina", code: "AR", lat: -34.6037, lng: -58.3816 },
  { name: "Lima", country: "Peru", code: "PE", lat: -12.0464, lng: -77.0428 },
  { name: "Bogotá", country: "Colombia", code: "CO", lat: 4.7110, lng: -74.0721 },
  { name: "Cartagena", country: "Colombia", code: "CO", lat: 10.3910, lng: -75.4794 },
  { name: "Santiago", country: "Chile", code: "CL", lat: -33.4489, lng: -70.6693 },
  
  // Africa & Middle East
  { name: "Cape Town", country: "South Africa", code: "ZA", lat: -33.9249, lng: 18.4241 },
  { name: "Johannesburg", country: "South Africa", code: "ZA", lat: -26.2041, lng: 28.0473 },
  { name: "Marrakech", country: "Morocco", code: "MA", lat: 31.6295, lng: -7.9811 },
  { name: "Cairo", country: "Egypt", code: "EG", lat: 30.0444, lng: 31.2357 },
  { name: "Nairobi", country: "Kenya", code: "KE", lat: -1.2921, lng: 36.8219 },
  { name: "Tel Aviv", country: "Israel", code: "IL", lat: 32.0853, lng: 34.7818 },
  { name: "Jerusalem", country: "Israel", code: "IL", lat: 31.7683, lng: 35.2137 },
  { name: "Amman", country: "Jordan", code: "JO", lat: 31.9454, lng: 35.9284 },
  { name: "Doha", country: "Qatar", code: "QA", lat: 25.2854, lng: 51.5310 },
  { name: "Muscat", country: "Oman", code: "OM", lat: 23.5880, lng: 58.3829 },
  
  // Oceania
  { name: "Sydney", country: "Australia", code: "AU", lat: -33.8688, lng: 151.2093 },
  { name: "Melbourne", country: "Australia", code: "AU", lat: -37.8136, lng: 144.9631 },
  { name: "Brisbane", country: "Australia", code: "AU", lat: -27.4698, lng: 153.0251 },
  { name: "Perth", country: "Australia", code: "AU", lat: -31.9505, lng: 115.8605 },
  { name: "Auckland", country: "New Zealand", code: "NZ", lat: -36.8509, lng: 174.7645 },
  { name: "Queenstown", country: "New Zealand", code: "NZ", lat: -45.0312, lng: 168.6626 },
  
  // Caribbean
  { name: "Kingston", country: "Jamaica", code: "JM", lat: 17.9714, lng: -76.7936 },
  { name: "Montego Bay", country: "Jamaica", code: "JM", lat: 18.4762, lng: -77.8939 },
  { name: "Ocho Rios", country: "Jamaica", code: "JM", lat: 18.4074, lng: -77.1025 },
  { name: "Negril", country: "Jamaica", code: "JM", lat: 18.2683, lng: -78.3550 },
  { name: "Nassau", country: "Bahamas", code: "BS", lat: 25.0343, lng: -77.3963 },
  { name: "San Juan", country: "Puerto Rico", code: "PR", lat: 18.4655, lng: -66.1057 },
  { name: "Bridgetown", country: "Barbados", code: "BB", lat: 13.1132, lng: -59.5988 },
  { name: "Port of Spain", country: "Trinidad and Tobago", code: "TT", lat: 10.6596, lng: -61.5086 },
  { name: "Punta Cana", country: "Dominican Republic", code: "DO", lat: 18.5601, lng: -68.3725 },
  { name: "Santo Domingo", country: "Dominican Republic", code: "DO", lat: 18.4861, lng: -69.9312 },
  { name: "Aruba", country: "Aruba", code: "AW", lat: 12.5211, lng: -69.9683 },
  { name: "Curaçao", country: "Curaçao", code: "CW", lat: 12.1696, lng: -68.9900 },
  { name: "Grand Cayman", country: "Cayman Islands", code: "KY", lat: 19.3133, lng: -81.2546 },
  { name: "St. Lucia", country: "Saint Lucia", code: "LC", lat: 13.9094, lng: -60.9789 },
  { name: "Antigua", country: "Antigua and Barbuda", code: "AG", lat: 17.1274, lng: -61.8468 },
  
  // More African cities
  { name: "Lagos", country: "Nigeria", code: "NG", lat: 6.5244, lng: 3.3792 },
  { name: "Accra", country: "Ghana", code: "GH", lat: 5.6037, lng: -0.1870 },
  { name: "Addis Ababa", country: "Ethiopia", code: "ET", lat: 9.0320, lng: 38.7469 },
  { name: "Casablanca", country: "Morocco", code: "MA", lat: 33.5731, lng: -7.5898 },
  { name: "Zanzibar", country: "Tanzania", code: "TZ", lat: -6.1659, lng: 39.2026 },
  { name: "Victoria Falls", country: "Zimbabwe", code: "ZW", lat: -17.9316, lng: 25.8303 },
  { name: "Luxor", country: "Egypt", code: "EG", lat: 25.6872, lng: 32.6396 },
  { name: "Fez", country: "Morocco", code: "MA", lat: 34.0181, lng: -5.0078 },
  
  // More Asian cities
  { name: "Siem Reap", country: "Cambodia", code: "KH", lat: 13.3633, lng: 103.8560 },
  { name: "Da Nang", country: "Vietnam", code: "VN", lat: 16.0544, lng: 108.2022 },
  { name: "Hoi An", country: "Vietnam", code: "VN", lat: 15.8801, lng: 108.3380 },
  { name: "Luang Prabang", country: "Laos", code: "LA", lat: 19.8856, lng: 102.1346 },
  { name: "Yangon", country: "Myanmar", code: "MM", lat: 16.8661, lng: 96.1951 },
  { name: "Kochi", country: "India", code: "IN", lat: 9.9312, lng: 76.2673 },
  { name: "Udaipur", country: "India", code: "IN", lat: 24.5854, lng: 73.7125 },
  { name: "Varanasi", country: "India", code: "IN", lat: 25.3176, lng: 82.9739 },
  { name: "Penang", country: "Malaysia", code: "MY", lat: 5.4164, lng: 100.3327 },
  { name: "Langkawi", country: "Malaysia", code: "MY", lat: 6.3500, lng: 99.8000 },
  { name: "Nara", country: "Japan", code: "JP", lat: 34.6851, lng: 135.8048 },
  { name: "Hiroshima", country: "Japan", code: "JP", lat: 34.3853, lng: 132.4553 },
  { name: "Busan", country: "South Korea", code: "KR", lat: 35.1796, lng: 129.0756 },
  { name: "Jeju", country: "South Korea", code: "KR", lat: 33.4996, lng: 126.5312 },
  
  // More European cities
  { name: "Nice", country: "France", code: "FR", lat: 43.7102, lng: 7.2620 },
  { name: "Lyon", country: "France", code: "FR", lat: 45.7640, lng: 4.8357 },
  { name: "Marseille", country: "France", code: "FR", lat: 43.2965, lng: 5.3698 },
  { name: "Bordeaux", country: "France", code: "FR", lat: 44.8378, lng: -0.5792 },
  { name: "Seville", country: "Spain", code: "ES", lat: 37.3891, lng: -5.9845 },
  { name: "Valencia", country: "Spain", code: "ES", lat: 39.4699, lng: -0.3763 },
  { name: "Granada", country: "Spain", code: "ES", lat: 37.1773, lng: -3.5986 },
  { name: "Porto", country: "Portugal", code: "PT", lat: 41.1579, lng: -8.6291 },
  { name: "Dubrovnik", country: "Croatia", code: "HR", lat: 42.6507, lng: 18.0944 },
  { name: "Split", country: "Croatia", code: "HR", lat: 43.5081, lng: 16.4402 },
  { name: "Krakow", country: "Poland", code: "PL", lat: 50.0647, lng: 19.9450 },
  { name: "Bruges", country: "Belgium", code: "BE", lat: 51.2093, lng: 3.2247 },
  { name: "Salzburg", country: "Austria", code: "AT", lat: 47.8095, lng: 13.0550 },
  { name: "Interlaken", country: "Switzerland", code: "CH", lat: 46.6863, lng: 7.8632 },
  { name: "Lucerne", country: "Switzerland", code: "CH", lat: 47.0502, lng: 8.3093 },
  { name: "Bergen", country: "Norway", code: "NO", lat: 60.3913, lng: 5.3221 },
  { name: "Tromsø", country: "Norway", code: "NO", lat: 69.6492, lng: 18.9553 },
  { name: "Mykonos", country: "Greece", code: "GR", lat: 37.4467, lng: 25.3289 },
  { name: "Crete", country: "Greece", code: "GR", lat: 35.2401, lng: 24.8093 },
  { name: "Cappadocia", country: "Turkey", code: "TR", lat: 38.6431, lng: 34.8289 },
  { name: "Antalya", country: "Turkey", code: "TR", lat: 36.8969, lng: 30.7133 },
  
  // More Americas cities
  { name: "Nashville", country: "United States", code: "US", lat: 36.1627, lng: -86.7816 },
  { name: "Austin", country: "United States", code: "US", lat: 30.2672, lng: -97.7431 },
  { name: "Denver", country: "United States", code: "US", lat: 39.7392, lng: -104.9903 },
  { name: "Portland", country: "United States", code: "US", lat: 45.5155, lng: -122.6789 },
  { name: "San Diego", country: "United States", code: "US", lat: 32.7157, lng: -117.1611 },
  { name: "Savannah", country: "United States", code: "US", lat: 32.0809, lng: -81.0912 },
  { name: "Charleston", country: "United States", code: "US", lat: 32.7765, lng: -79.9311 },
  { name: "Aspen", country: "United States", code: "US", lat: 39.1911, lng: -106.8175 },
  { name: "Calgary", country: "Canada", code: "CA", lat: 51.0447, lng: -114.0719 },
  { name: "Quebec City", country: "Canada", code: "CA", lat: 46.8139, lng: -71.2080 },
  { name: "Banff", country: "Canada", code: "CA", lat: 51.1784, lng: -115.5708 },
  { name: "Playa del Carmen", country: "Mexico", code: "MX", lat: 20.6296, lng: -87.0739 },
  { name: "Tulum", country: "Mexico", code: "MX", lat: 20.2114, lng: -87.4654 },
  { name: "Puerto Vallarta", country: "Mexico", code: "MX", lat: 20.6534, lng: -105.2253 },
  { name: "Medellín", country: "Colombia", code: "CO", lat: 6.2442, lng: -75.5812 },
  { name: "Cusco", country: "Peru", code: "PE", lat: -13.5320, lng: -71.9675 },
  { name: "Montevideo", country: "Uruguay", code: "UY", lat: -34.9011, lng: -56.1645 },
  { name: "Patagonia", country: "Argentina", code: "AR", lat: -41.8101, lng: -68.9063 },
  
  // Pacific Islands
  { name: "Bora Bora", country: "French Polynesia", code: "PF", lat: -16.5004, lng: -151.7415 },
  { name: "Tahiti", country: "French Polynesia", code: "PF", lat: -17.6509, lng: -149.4260 },
  { name: "Fiji Islands", country: "Fiji", code: "FJ", lat: -17.7134, lng: 178.0650 },
  { name: "Palau", country: "Palau", code: "PW", lat: 7.5150, lng: 134.5825 },
];

// All countries with codes
const COUNTRIES = [
  // A
  { name: "Afghanistan", code: "AF" }, { name: "Albania", code: "AL" }, { name: "Algeria", code: "DZ" },
  { name: "Andorra", code: "AD" }, { name: "Angola", code: "AO" }, { name: "Antigua and Barbuda", code: "AG" },
  { name: "Argentina", code: "AR" }, { name: "Armenia", code: "AM" }, { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" }, { name: "Azerbaijan", code: "AZ" },
  // B
  { name: "Bahamas", code: "BS" }, { name: "Bahrain", code: "BH" }, { name: "Bangladesh", code: "BD" },
  { name: "Barbados", code: "BB" }, { name: "Belarus", code: "BY" }, { name: "Belgium", code: "BE" },
  { name: "Belize", code: "BZ" }, { name: "Benin", code: "BJ" }, { name: "Bhutan", code: "BT" },
  { name: "Bolivia", code: "BO" }, { name: "Bosnia and Herzegovina", code: "BA" }, { name: "Botswana", code: "BW" },
  { name: "Brazil", code: "BR" }, { name: "Brunei", code: "BN" }, { name: "Bulgaria", code: "BG" },
  { name: "Burkina Faso", code: "BF" }, { name: "Burundi", code: "BI" },
  // C
  { name: "Cabo Verde", code: "CV" }, { name: "Cape Verde", code: "CV" }, { name: "Cambodia", code: "KH" },
  { name: "Cameroon", code: "CM" }, { name: "Canada", code: "CA" }, { name: "Central African Republic", code: "CF" },
  { name: "Chad", code: "TD" }, { name: "Chile", code: "CL" }, { name: "China", code: "CN" },
  { name: "Colombia", code: "CO" }, { name: "Comoros", code: "KM" }, { name: "Congo", code: "CG" },
  { name: "Costa Rica", code: "CR" }, { name: "Côte d'Ivoire", code: "CI" }, { name: "Ivory Coast", code: "CI" },
  { name: "Croatia", code: "HR" }, { name: "Cuba", code: "CU" }, { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" }, { name: "Czechia", code: "CZ" },
  // D
  { name: "Denmark", code: "DK" }, { name: "Djibouti", code: "DJ" }, { name: "Dominica", code: "DM" },
  { name: "Dominican Republic", code: "DO" },
  // E
  { name: "Ecuador", code: "EC" }, { name: "Egypt", code: "EG" }, { name: "El Salvador", code: "SV" },
  { name: "Equatorial Guinea", code: "GQ" }, { name: "Eritrea", code: "ER" }, { name: "Estonia", code: "EE" },
  { name: "Eswatini", code: "SZ" }, { name: "Swaziland", code: "SZ" }, { name: "Ethiopia", code: "ET" },
  // F
  { name: "Fiji", code: "FJ" }, { name: "Finland", code: "FI" }, { name: "France", code: "FR" },
  // G
  { name: "Gabon", code: "GA" }, { name: "Gambia", code: "GM" }, { name: "Georgia", code: "GE" },
  { name: "Germany", code: "DE" }, { name: "Ghana", code: "GH" }, { name: "Greece", code: "GR" },
  { name: "Grenada", code: "GD" }, { name: "Guatemala", code: "GT" }, { name: "Guinea", code: "GN" },
  { name: "Guinea-Bissau", code: "GW" }, { name: "Guyana", code: "GY" },
  // H
  { name: "Haiti", code: "HT" }, { name: "Honduras", code: "HN" }, { name: "Hong Kong", code: "HK" },
  { name: "Hungary", code: "HU" },
  // I
  { name: "Iceland", code: "IS" }, { name: "India", code: "IN" }, { name: "Indonesia", code: "ID" },
  { name: "Iran", code: "IR" }, { name: "Iraq", code: "IQ" }, { name: "Ireland", code: "IE" },
  { name: "Israel", code: "IL" }, { name: "Italy", code: "IT" },
  // J
  { name: "Jamaica", code: "JM" }, { name: "Japan", code: "JP" }, { name: "Jordan", code: "JO" },
  // K
  { name: "Kazakhstan", code: "KZ" }, { name: "Kenya", code: "KE" }, { name: "Kiribati", code: "KI" },
  { name: "Kuwait", code: "KW" }, { name: "Kyrgyzstan", code: "KG" },
  // L
  { name: "Laos", code: "LA" }, { name: "Latvia", code: "LV" }, { name: "Lebanon", code: "LB" },
  { name: "Lesotho", code: "LS" }, { name: "Liberia", code: "LR" }, { name: "Libya", code: "LY" },
  { name: "Liechtenstein", code: "LI" }, { name: "Lithuania", code: "LT" }, { name: "Luxembourg", code: "LU" },
  // M
  { name: "Macau", code: "MO" }, { name: "Madagascar", code: "MG" }, { name: "Malawi", code: "MW" },
  { name: "Malaysia", code: "MY" }, { name: "Maldives", code: "MV" }, { name: "Mali", code: "ML" },
  { name: "Malta", code: "MT" }, { name: "Marshall Islands", code: "MH" }, { name: "Mauritania", code: "MR" },
  { name: "Mauritius", code: "MU" }, { name: "Mexico", code: "MX" }, { name: "Micronesia", code: "FM" },
  { name: "Moldova", code: "MD" }, { name: "Monaco", code: "MC" }, { name: "Mongolia", code: "MN" },
  { name: "Montenegro", code: "ME" }, { name: "Morocco", code: "MA" }, { name: "Mozambique", code: "MZ" },
  { name: "Myanmar", code: "MM" }, { name: "Burma", code: "MM" },
  // N
  { name: "Namibia", code: "NA" }, { name: "Nauru", code: "NR" }, { name: "Nepal", code: "NP" },
  { name: "Netherlands", code: "NL" }, { name: "New Zealand", code: "NZ" }, { name: "Nicaragua", code: "NI" },
  { name: "Niger", code: "NE" }, { name: "Nigeria", code: "NG" }, { name: "North Korea", code: "KP" },
  { name: "North Macedonia", code: "MK" }, { name: "Macedonia", code: "MK" }, { name: "Norway", code: "NO" },
  // O
  { name: "Oman", code: "OM" },
  // P
  { name: "Pakistan", code: "PK" }, { name: "Palau", code: "PW" }, { name: "Palestine", code: "PS" },
  { name: "Panama", code: "PA" }, { name: "Papua New Guinea", code: "PG" }, { name: "Paraguay", code: "PY" },
  { name: "Peru", code: "PE" }, { name: "Philippines", code: "PH" }, { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" }, { name: "Puerto Rico", code: "PR" },
  // Q
  { name: "Qatar", code: "QA" },
  // R
  { name: "Romania", code: "RO" }, { name: "Russia", code: "RU" }, { name: "Rwanda", code: "RW" },
  // S
  { name: "Saint Kitts and Nevis", code: "KN" }, { name: "Saint Lucia", code: "LC" },
  { name: "Saint Vincent and the Grenadines", code: "VC" }, { name: "Samoa", code: "WS" },
  { name: "San Marino", code: "SM" }, { name: "São Tomé and Príncipe", code: "ST" },
  { name: "Saudi Arabia", code: "SA" }, { name: "Senegal", code: "SN" }, { name: "Serbia", code: "RS" },
  { name: "Seychelles", code: "SC" }, { name: "Sierra Leone", code: "SL" }, { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" }, { name: "Slovenia", code: "SI" }, { name: "Solomon Islands", code: "SB" },
  { name: "Somalia", code: "SO" }, { name: "South Africa", code: "ZA" }, { name: "South Korea", code: "KR" },
  { name: "South Sudan", code: "SS" }, { name: "Spain", code: "ES" }, { name: "Sri Lanka", code: "LK" },
  { name: "Sudan", code: "SD" }, { name: "Suriname", code: "SR" }, { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" }, { name: "Syria", code: "SY" },
  // T
  { name: "Taiwan", code: "TW" }, { name: "Tajikistan", code: "TJ" }, { name: "Tanzania", code: "TZ" },
  { name: "Thailand", code: "TH" }, { name: "Timor-Leste", code: "TL" }, { name: "East Timor", code: "TL" },
  { name: "Togo", code: "TG" }, { name: "Tonga", code: "TO" }, { name: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", code: "TN" }, { name: "Turkey", code: "TR" }, { name: "Türkiye", code: "TR" },
  { name: "Turkmenistan", code: "TM" }, { name: "Tuvalu", code: "TV" },
  // U
  { name: "Uganda", code: "UG" }, { name: "Ukraine", code: "UA" }, { name: "UAE", code: "AE" },
  { name: "United Arab Emirates", code: "AE" }, { name: "United Kingdom", code: "GB" }, { name: "UK", code: "GB" },
  { name: "England", code: "GB" }, { name: "Scotland", code: "GB" }, { name: "Wales", code: "GB" },
  { name: "United States", code: "US" }, { name: "USA", code: "US" }, { name: "America", code: "US" },
  { name: "Uruguay", code: "UY" }, { name: "Uzbekistan", code: "UZ" },
  // V
  { name: "Vanuatu", code: "VU" }, { name: "Vatican City", code: "VA" }, { name: "Venezuela", code: "VE" },
  { name: "Vietnam", code: "VN" },
  // Y
  { name: "Yemen", code: "YE" },
  // Z
  { name: "Zambia", code: "ZM" }, { name: "Zimbabwe", code: "ZW" },
  // Territories & Special Regions
  { name: "Aruba", code: "AW" }, { name: "Bermuda", code: "BM" }, { name: "Cayman Islands", code: "KY" },
  { name: "Curaçao", code: "CW" }, { name: "Curacao", code: "CW" }, { name: "Faroe Islands", code: "FO" },
  { name: "French Polynesia", code: "PF" }, { name: "Tahiti", code: "PF" }, { name: "Bora Bora", code: "PF" },
  { name: "Gibraltar", code: "GI" }, { name: "Greenland", code: "GL" }, { name: "Guadeloupe", code: "GP" },
  { name: "Guam", code: "GU" }, { name: "Martinique", code: "MQ" }, { name: "New Caledonia", code: "NC" },
  { name: "Réunion", code: "RE" }, { name: "Reunion", code: "RE" }, { name: "Saint Martin", code: "MF" },
  { name: "Sint Maarten", code: "SX" }, { name: "St. Maarten", code: "SX" }, { name: "Turks and Caicos", code: "TC" },
  { name: "U.S. Virgin Islands", code: "VI" }, { name: "British Virgin Islands", code: "VG" },
  // Sovereign countries previously missing
  { name: "Democratic Republic of the Congo", code: "CD" }, { name: "DR Congo", code: "CD" }, { name: "DRC", code: "CD" }, { name: "Congo (Kinshasa)", code: "CD" },
  { name: "Western Sahara", code: "EH" },
  // Additional ISO 3166-1 territories & dependencies
  { name: "Anguilla", code: "AI" }, { name: "American Samoa", code: "AS" }, { name: "Åland Islands", code: "AX" }, { name: "Aland Islands", code: "AX" },
  { name: "Saint Barthélemy", code: "BL" }, { name: "Saint Barthelemy", code: "BL" }, { name: "Caribbean Netherlands", code: "BQ" }, { name: "Bonaire", code: "BQ" },
  { name: "Cocos Islands", code: "CC" }, { name: "Cook Islands", code: "CK" }, { name: "Christmas Island", code: "CX" },
  { name: "Falkland Islands", code: "FK" }, { name: "French Guiana", code: "GF" }, { name: "Guernsey", code: "GG" },
  { name: "South Georgia", code: "GS" }, { name: "Isle of Man", code: "IM" }, { name: "British Indian Ocean Territory", code: "IO" },
  { name: "Jersey", code: "JE" }, { name: "Northern Mariana Islands", code: "MP" }, { name: "Montserrat", code: "MS" },
  { name: "Norfolk Island", code: "NF" }, { name: "Niue", code: "NU" }, { name: "Saint Pierre and Miquelon", code: "PM" },
  { name: "Pitcairn Islands", code: "PN" }, { name: "Saint Helena", code: "SH" }, { name: "Svalbard and Jan Mayen", code: "SJ" },
  { name: "French Southern Territories", code: "TF" }, { name: "Tokelau", code: "TK" }, { name: "Wallis and Futuna", code: "WF" },
  { name: "Mayotte", code: "YT" }, { name: "Antarctica", code: "AQ" },
  // Disputed / partially recognised states
  { name: "Kosovo", code: "XK" }, { name: "Somaliland", code: "SO_LAND" }, { name: "Northern Cyprus", code: "CY_NORTH" },
  // Uninhabited / Antarctic territories (rarely visited but ISO-recognised)
  { name: "Bouvet Island", code: "BV" }, { name: "Heard Island and McDonald Islands", code: "HM" }, { name: "US Minor Outlying Islands", code: "UM" },
];

export const LocationPicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("destinations"); // destinations, cities, countries
  const containerRef = useRef(null);

  // Memoize filtered arrays to ensure they recalculate when search changes
  const filteredDestinations = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return POPULAR_DESTINATIONS;
    return POPULAR_DESTINATIONS.filter(
      d => d.name.toLowerCase().includes(searchLower) ||
           d.country.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const filteredCities = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return CITIES.slice(0, 50);
    return CITIES.filter(
      c => c.name.toLowerCase().includes(searchLower) ||
           c.country.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const filteredCountries = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return COUNTRIES.slice(0, 50);
    return COUNTRIES.filter(
      c => c.name.toLowerCase().includes(searchLower) ||
           c.code.toLowerCase() === searchLower
    );
  }, [search]);

  const handleSelect = (item, type) => {
    let data;
    if (type === "destination" || type === "city") {
      data = {
        location: item.name,
        country: item.country,
        country_code: item.code,
        latitude: item.lat,
        longitude: item.lng
      };
    } else {
      // Country only
      data = {
        location: null,
        country: item.name,
        country_code: item.code,
        latitude: null,
        longitude: null
      };
    }
    onChange(data);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = () => {
    onChange({
      location: null,
      country: null,
      country_code: null,
      latitude: null,
      longitude: null
    });
  };

  const [suggestOpen, setSuggestOpen] = useState(false);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayValue = value?.location 
    ? `${value.location}, ${value.country}` 
    : value?.country || "";

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => setIsOpen(true)}
        className="flex items-center h-12 px-4 bg-background border border-charcoal/10 rounded-xl cursor-pointer hover:border-charcoal/20 transition-colors"
      >
        <MapPin className="w-5 h-5 text-muted-foreground mr-3" />
        {displayValue ? (
          <div className="flex-1 flex items-center justify-between">
            <span className="text-charcoal">{displayValue}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-charcoal/5 rounded"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        ) : (
          <span className="text-muted-foreground">Add destination (optional)</span>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-charcoal/10 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-charcoal/5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onInput={(e) => setSearch(e.target.value)}
                placeholder="Search destinations, cities or countries..."
                className="w-full h-10 pl-9 pr-3 bg-background border border-charcoal/10 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary/50"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                data-testid="location-search-input"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-charcoal/5 overflow-x-auto">
            <button
              type="button"
              onClick={() => setTab("destinations")}
              className={`flex-1 min-w-max py-2 px-3 text-sm font-medium transition-colors ${
                tab === "destinations" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
              }`}
            >
              <MapPin className="w-4 h-4 inline mr-1" />
              Popular
            </button>
            <button
              type="button"
              onClick={() => setTab("cities")}
              className={`flex-1 min-w-max py-2 px-3 text-sm font-medium transition-colors ${
                tab === "cities" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
              }`}
            >
              <Building2 className="w-4 h-4 inline mr-1" />
              Cities
            </button>
            <button
              type="button"
              onClick={() => setTab("countries")}
              className={`flex-1 min-w-max py-2 px-3 text-sm font-medium transition-colors ${
                tab === "countries" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
              }`}
            >
              <Globe className="w-4 h-4 inline mr-1" />
              Countries
            </button>
          </div>

          {/* Results - key forces re-render when search changes */}
          <ScrollArea className="max-h-64" key={`results-${tab}-${search}`}>
            <div className="p-2">
              {tab === "destinations" ? (
                filteredDestinations.length > 0 ? (
                  filteredDestinations.map((dest) => (
                    <button
                      type="button"
                      key={`dest-${dest.name}-${dest.country}`}
                      onClick={() => handleSelect(dest, "destination")}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-background transition-colors text-left"
                      data-testid={`destination-${dest.name}`}
                    >
                      <MapPin className="w-4 h-4 text-primary" />
                      <div>
                        <div className="text-sm font-medium text-charcoal">{dest.name}</div>
                        <div className="text-xs text-muted-foreground">{dest.country}</div>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No destinations found</p>
                )
              ) : tab === "cities" ? (
                filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <button
                      type="button"
                      key={`city-${city.name}-${city.country}`}
                      onClick={() => handleSelect(city, "city")}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-background transition-colors text-left"
                      data-testid={`city-${city.name}`}
                    >
                      <Building2 className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="text-sm font-medium text-charcoal">{city.name}</div>
                        <div className="text-xs text-muted-foreground">{city.country}</div>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No cities found</p>
                )
              ) : (
                filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      type="button"
                      key={`country-${country.code}`}
                      onClick={() => handleSelect(country, "country")}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-background transition-colors text-left"
                      data-testid={`country-${country.code}`}
                    >
                      <Globe className="w-4 h-4 text-secondary" />
                      <span className="text-sm font-medium text-charcoal">{country.name}</span>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No countries found</p>
                )
              )}
            </div>
          </ScrollArea>

          {/* Suggest a missing place footer */}
          <button
            type="button"
            onClick={() => setSuggestOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-charcoal/5 text-sm text-primary hover:bg-primary/5 transition-colors font-medium"
            data-testid="location-suggest-place-btn"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Can't find it? Suggest a missing place
          </button>
        </div>
      )}

      <SuggestPlaceModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        prefilledQuery={search}
      />
    </div>
  );
};

export default LocationPicker;
